import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserPool,
    CognitoUserSession,
    ICognitoStorage
} from 'amazon-cognito-identity-js';
import {
    HANDOFF_ERROR_CODES,
    HandoffError
} from "./HandoffError.ts";

export type SrpSignInResult = {
    username: string,
    accessToken: string,
    refreshToken: string,
};

// Keeps the library's token cache in memory so nothing reaches localStorage.
export class InMemoryStorage implements ICognitoStorage {
    private items = new Map<string, string>();

    setItem(key: string, value: string) {
        this.items.set(key, value);
    }

    getItem(key: string) {
        return this.items.get(key) ?? null;
    }

    removeItem(key: string) {
        this.items.delete(key);
    }

    clear() {
        this.items.clear();
    }
}

export const signInWithPassword = ({
                                       poolId,
                                       clientId,
                                       email,
                                       password,
                                   }: {
    poolId: string,
    clientId: string,
    email: string,
    password: string,
}): Promise<SrpSignInResult> => {
    const storage = new InMemoryStorage();
    const pool = new CognitoUserPool({
                                         UserPoolId: poolId,
                                         ClientId: clientId,
                                         Storage: storage,
                                     });
    const user = new CognitoUser({
                                     Username: email,
                                     Pool: pool,
                                     Storage: storage,
                                 });
    const details = new AuthenticationDetails({
                                                  Username: email,
                                                  Password: password,
                                              });

    return new Promise((resolve, reject) => {
        const unsupported = () => {
            storage.clear();
            reject(new HandoffError(HANDOFF_ERROR_CODES.unsupportedChallenge));
        };

        user.authenticateUser(details, {
            onSuccess: (session: CognitoUserSession) => {
                storage.clear();
                const accessToken = session.getAccessToken();
                resolve({
                            username: accessToken.payload.username as string,
                            accessToken: accessToken.getJwtToken(),
                            refreshToken: session.getRefreshToken()
                                                 .getToken(),
                        });
            },
            onFailure: (err: { code?: string, name?: string, message?: string }) => {
                storage.clear();
                reject(new HandoffError(err?.code ?? err?.name ?? 'UnknownError', err?.message));
            },
            newPasswordRequired: unsupported,
            mfaRequired: unsupported,
            totpRequired: unsupported,
            customChallenge: unsupported,
            mfaSetup: unsupported,
            selectMFAType: unsupported,
        });
    });
};
